import type {PointerPosition} from "../ui/components/Pointer";


export type MenuPlacement = 'block-end' | 'block-start' | 'inline-end' | 'inline-start';
export type MenuAlignment = 'start' | 'center' | 'end';


export interface NormalizePositionProps {
    mouseX?: number;
    mouseY?: number;
    contextMenu: HTMLElement;
    placement?: MenuPlacement;
    alignment?: MenuAlignment;
    withPointer?: boolean;
    targetBoundingBox?: DOMRect;
    VERTICAL_SHIFT?: number,
    HORIZONTAL_SHIFT?: number,
    isPointerLarge?: boolean,
    getScopeBoundingClientRect?: () => DOMRect,
    scopeAdditionalPadding?: number,
}

export interface ResultPosition {
    left: number;
    top: number;
    actualDirection: MenuPlacement,
    width: number,
    height: number;
    pointerPosition?: PointerPosition;
}

/* Pointer size */
const P_long = 10;
const P_short = 4;
const P_long_large = 24;
const P_short_large = 10;

export function normalizePosition({
                                      mouseX,
                                      mouseY,
                                      contextMenu,
                                      placement = 'block-end',
                                      alignment = 'start',
                                      targetBoundingBox,
                                      withPointer = false,
                                      VERTICAL_SHIFT,
                                      HORIZONTAL_SHIFT,
                                      isPointerLarge = false,
                                      getScopeBoundingClientRect = document.body.getBoundingClientRect,
                                      scopeAdditionalPadding = 0,
                                  }: NormalizePositionProps): ResultPosition {
    const Tx = targetBoundingBox ? targetBoundingBox!.x : mouseX!;
    const Ty = targetBoundingBox ? targetBoundingBox!.y : mouseY!;
    const Tw = targetBoundingBox ? targetBoundingBox!.width : 0;
    const Th = targetBoundingBox ? targetBoundingBox!.height : 0;
    let [PLong, PShort] = isPointerLarge ? [P_long_large, P_short_large] : [P_long, P_short];
    if (!withPointer) {
        PLong = 0;
        PShort = 0;
    }

    if (HORIZONTAL_SHIFT === undefined) {
        if (placement === 'inline-end' || placement === 'inline-start') {
            HORIZONTAL_SHIFT = 0;
        } else {
            HORIZONTAL_SHIFT = 16;
        }
    }
    if (VERTICAL_SHIFT === undefined) {
        if (placement === 'block-end' || placement === 'block-start') {
            VERTICAL_SHIFT = 0;
        } else {
            VERTICAL_SHIFT = 16;
        }
    }

    const {
        left,
        top,
        actualDirection,
        width,
        height,
    } = normalizeWithScope({
        Tx,
        Ty,
        Tw,
        Th,
        contextMenu,
        placement: placement,
        alignment: alignment,
        HORIZONTAL_SHIFT,
        VERTICAL_SHIFT,
        PShort,
        getScopeBoundingClientRect,
        scopeAdditionalPadding,
    });

    let pointerPosition: PointerPosition | undefined = undefined;
    if (withPointer) {
        const {left: Sx, top: Sy} = getScopeBoundingClientRect();
        const y_shift = 0 - Sy;
        const x_shift = 0 - Sx;
        pointerPosition = calculatePointerPosition(Tx + x_shift, Ty + y_shift, Tw, Th, actualDirection,
            HORIZONTAL_SHIFT,
            VERTICAL_SHIFT,
            PLong, PShort
        );
    }

    return {
        left,
        top,
        width,
        height,
        actualDirection,
        pointerPosition,

    };
}

interface NormalizeParams {
    Tx: number,
    Ty: number,
    Tw: number,
    Th: number,
    contextMenu: HTMLElement,
    placement: MenuPlacement,
    alignment: MenuAlignment,
    VERTICAL_SHIFT: number,
    HORIZONTAL_SHIFT: number,
    PShort: number,
    getScopeBoundingClientRect: () => DOMRect,
    scopeAdditionalPadding?: number,
}

interface NormalizeReturn {
    left: number,
    top: number,
    height: number,
    width: number,
    actualDirection: MenuPlacement,
}

/*
                          Sx                              <-- Sw -->
                        Sy┌──────────────────────────────────────────────────────────────────────────┐
                          │ Scope                                                                 ^  │
                          │                                                                       |  │
                          │                                                                       |  │
                          │                                                                ScrollTop │
                          │                                                                       |  │
                          │                                                                       |  │
   0                      │                                                                       v  │
  0┌─────────────────────────────────────────────────────────────────────────────────────────────────────────────┐
   │ ScreenView           │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                       ^  │           │
   │                      │        Mx    <-- Mw -->                                               |  │           │
   │                      │      My┌───────────────────────┐                                      Sh │           │
   │                      │        │ Menu                  │                                      |  │           │
   │                      │  Tx    │                    ^  │                                      v  │           │
   │                      │Ty┌─┐ P │                    |  │                                         │           │
   │                      │  │T│<<<│                    Mh │                                         │           │
   │                      │  └─┘   │                    |  │                                         │           │
   │                      │        │                    v  │                                         │           │
   │                      │        │                       │                                         │           │
   │                      │        └───────────────────────┘                                         │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      │                                                                          │           │
   │                      └──────────────────────────────────────────────────────────────────────────┘           │
   │                                                                                                             │
   └─────────────────────────────────────────────────────────────────────────────────────────────────────────────┘
                             illustration of inline end MenuPlacement and center MenuAlignment
    M - menu
    T - target : relative to viewPort (like fixed position) got by getBoundingBoxRect
    S - scope : can have not 0 Sx and Sy and have Scroll
    P - pointer
    w - width
    h - height
    x - left
    y - top

    calculation logic:
    screen is document.clientHeight (browser window actual height)
    scopes max scroll === scope.offsetHeight - screen.clientHeight, so
    0 <= scope.scrollTop <= scope.offsetHeight - screen.clientHeight

    fitting logic should check whether Menu fits in screen, so we will make Mx/My relative to screen start
    to fix final Mx, MY we will add scrollTop back to it.

    as overflow-y causes it's staking context and prevent elements inside to overlap itself - we are forced
    to make context menu target parent outside the scrollable part.
    Note: To visually not break menu position on scroll, we shift context parent top to scrollTop value. It's counted as well on final result calculations
*/

/* exported for tests only */
export function normalizeWithScope(params: NormalizeParams, _previousResult?: NormalizeReturn): NormalizeReturn {
    const {
        Tx, Ty, Tw, Th,
        contextMenu, placement, alignment,
        HORIZONTAL_SHIFT, VERTICAL_SHIFT, getScopeBoundingClientRect,
        PShort,
        scopeAdditionalPadding = 0,
    } = params;
    const Mw = contextMenu.clientWidth;
    const Mh = contextMenu.clientHeight;

    // Compute desired coordinates for a given placement/alignment
    const placeAt = (p: MenuPlacement) =>
        getDesiredMenuCoordinates(p, alignment, Tx, Ty, Tw, Th, Mw, Mh, HORIZONTAL_SHIFT, VERTICAL_SHIFT, PShort);

    // Evaluate one attempt with strict or lenient adjustments
    const attempt = (p: MenuPlacement, strict: boolean) => {
        const {Mx, My} = placeAt(p);

        // screen/scope for fitting is the viewport minus header (same as before)
        const Sx = 0;
        const Sy = scopeAdditionalPadding;
        const Sw = document.documentElement.clientWidth;
        const Sh = document.documentElement.clientHeight - scopeAdditionalPadding;

        const fit = checkIfFittingWithAdjustment(
            Mx, My, Mw, Mh,
            Sx, Sy, Sw, Sh,
            p,
            HORIZONTAL_SHIFT, VERTICAL_SHIFT,
            strict
        );

        const {left: scopeLeft, top: scopeTop} = getScopeBoundingClientRect();
        const x_shift = -scopeLeft;
        const y_shift = -scopeTop;

        const leftAfter = Mx + fit.adjustment.x;
        const topAfter = My + fit.adjustment.y;

        // Overflow amounts after applying adjustment (in viewport coords)
        const screenLeft = Sx;
        const screenRight = Sx + Sw;
        const screenTop = Sy;
        const screenBottom = Sy + Sh;

        const overflowX =
            Math.max(screenLeft - leftAfter, 0) +
            Math.max((leftAfter + Mw) - screenRight, 0);

        const overflowY =
            Math.max(screenTop - topAfter, 0) +
            Math.max((topAfter + Mh) - screenBottom, 0);

        const isInline = (p === 'inline-end' || p === 'inline-start');
        const isFittingAxis = isInline ? (overflowX === 0) : (overflowY === 0);
        const mainOverflow = isInline ? overflowX : overflowY;

        const result: NormalizeReturn = {
            height: Mh,
            width: Mw,
            left: leftAfter + x_shift,
            top: topAfter + y_shift,
            actualDirection: p,
        };

        return {result, isFittingAxis, mainOverflow, overflowX, overflowY};
    };

    const flipped = reversePlacement(placement);

    // Try in sequence, return immediately on first success
    for (const [p, strict] of [
        [placement, true],
        [flipped, true],
        [placement, false],
        [flipped, false],
    ] as const) {
        const a = attempt(p, strict);
        if (a.isFittingAxis) return a.result;
    }

    // If none fit, re-evaluate all and pick minimal overflow
    const fallbacks = [
        attempt(placement, true),
        attempt(flipped, true),
        attempt(placement, false),
        attempt(flipped, false),
    ];
    const best = fallbacks.reduce((best, c) => {
        if (!best) return c;
        if (c.mainOverflow < best.mainOverflow) return c;
        if (c.mainOverflow === best.mainOverflow) {
            // tie-breaker: smaller total overflow
            const totalC = c.overflowX + c.overflowY;
            const totalB = best.overflowX + best.overflowY;
            return totalC < totalB ? c : best;
        }
        return best;
    }, null as unknown as typeof fallbacks[number]);

    return best.result;
}


export function getDesiredMenuCoordinates(
    placement: MenuPlacement,
    alignment: MenuAlignment,
    Tx: number, Ty: number, Tw: number, Th: number, Mw: number, Mh: number,
    HORIZONTAL_SHIFT: number, VERTICAL_SHIFT: number,
    PShort: number = P_short,
) {
    let Mx = Tx;
    switch (placement) {
        case "block-end":
        case "block-start":
            switch (alignment) {
                case "start":
                    Mx = Tx - HORIZONTAL_SHIFT;
                    break
                case "center":
                    Mx = getXForCentered(Tx, Tw, Mw);
                    break
                case "end":
                    Mx = Tx + Tw - Mw + HORIZONTAL_SHIFT
                    break
            }
            break;
        case "inline-end":
            Mx = Tx + Tw + PShort + HORIZONTAL_SHIFT;
            break;
        case "inline-start":
            Mx = Tx - Mw - PShort - HORIZONTAL_SHIFT;
            break;
    }

    let My = Ty;
    switch (placement) {
        case "block-end":
            My = Ty + Th + PShort + VERTICAL_SHIFT;
            break
        case "block-start":
            My = Ty - PShort - Mh - VERTICAL_SHIFT;
            break;
        case "inline-end":
        case "inline-start":
            switch (alignment) {
                case "start":
                    My = Ty - VERTICAL_SHIFT;
                    break
                case "center":
                    My = getXForCentered(Ty, Th, Mh);
                    break
                case "end":
                    My = Ty + Th - Mh + VERTICAL_SHIFT
                    break
            }
            break;
    }
    return {Mx: Mx, My}
}

export function checkIfFittingWithAdjustment(
    Mx: number, // Left position of the context menu
    My: number, // Top position of the context menu
    Mw: number, // Width of the context menu
    Mh: number, // Height of the context menu
    Sx: number, // Left position of the scope (container)
    Sy: number, // Top position of the scope (container)
    Sw: number, // Width of the scope (container)
    Sh: number, // Height of the scope (container)
    placement: MenuPlacement, // 'block-end' | 'block-start' | 'inline-end' | 'inline-start'
    HORIZONTAL_SHIFT: number = 0,
    VERTICAL_SHIFT: number = 0,
    limitAdjustmentToShift: boolean = true,
): {
    isFitting: boolean;
    adjustment: { x: number; y: number };
} {
    const adjustment = {x: 0, y: 0};

    // Horizontal clamp
    if (Mx < Sx) {
        adjustment.x = Sx - Mx;
    } else if (Mx + Mw > Sx + Sw) {
        adjustment.x = (Sx + Sw) - (Mx + Mw);
    }

    // Vertical clamp
    if (My < Sy) {
        adjustment.y = Sy - My;
    } else if (My + Mh > Sy + Sh) {
        adjustment.y = (Sy + Sh) - (My + Mh);
    }

    // Keep current behavior: on strict pass, do NOT allow large corrections on the main axis
    if (limitAdjustmentToShift) {
        // Inline: horizontal (note: original code only constrained 'inline-end';
        // to preserve behavior, we keep that asymmetry)
        if (placement === 'inline-end' && Math.abs(adjustment.x) > HORIZONTAL_SHIFT) {
            adjustment.x = 0;
        }
        // Block: vertical
        if ((placement === 'block-start' || placement === 'block-end') && Math.abs(adjustment.y) > VERTICAL_SHIFT) {
            adjustment.y = 0;
        }
    }

    // Axis-aware fit decision AFTER adjustments
    const leftAfter = Mx + adjustment.x;
    const rightAfter = leftAfter + Mw;
    const topAfter = My + adjustment.y;
    const bottomAfter = topAfter + Mh;

    const fitsX = leftAfter >= Sx && rightAfter <= (Sx + Sw);
    const fitsY = topAfter >= Sy && bottomAfter <= (Sy + Sh);

    const isInline = (placement === 'inline-end' || placement === 'inline-start');
    const isFitting = isInline ? fitsX : fitsY;

    return {isFitting, adjustment};
}

function reversePlacement(placement: MenuPlacement): MenuPlacement {
    switch (placement) {
        case "block-end":
            return "block-start";
        case "block-start":
            return "block-end";
        case "inline-end":
            return "inline-start";
        case "inline-start":
            return "inline-end";
    }
}

function calculatePointerPosition(Tx: number, Ty: number, Tw: number, Th: number, menuPlacement: MenuPlacement,
                                  HORIZONTAL_SHIFT: number,
                                  VERTICAL_SHIFT: number,
                                  PLong: number, PShort: number
): PointerPosition {
    switch (menuPlacement) {
        case "block-end":
            return {
                left: getXForCentered(Tx, Tw, PLong),
                top: Ty + Th + VERTICAL_SHIFT,
                direction: 'top'
            }
        case "block-start":
            return {
                left: getXForCentered(Tx, Tw, PLong),
                top: Ty - PShort - VERTICAL_SHIFT,
                direction: 'bottom'
            }
        case "inline-end":
            return {
                left: Tx + Tw + HORIZONTAL_SHIFT,
                top: getXForCentered(Ty, Th, PLong),
                direction: 'left'
            }
        case "inline-start":
            return {
                left: Tx - PShort - HORIZONTAL_SHIFT,
                top: getXForCentered(Ty, Th, PLong),
                direction: 'right'
            }
    }
}

function getXForCentered(Tx: number, Tw: number, Mw: number) {
    return (2 * Tx + Tw) / 2 - Mw / 2;
}